import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { CreditCard, Check, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Billing() {
  const { tenant } = useAuth();
  const location = useLocation();
  const [packages, setPackages] = useState([]);
  const [sub, setSub] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/packages").then(({ data }) => setPackages(data));
    api.get("/billing/subscription").then(({ data }) => setSub(data));
  }, []);

  // Poll for return-from-stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setStatus("checking");
    const poll = async () => {
      attempts += 1;
      if (attempts > 6) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          api.get("/billing/subscription").then(({ data }) => setSub(data));
        } else if (data.status === "expired") { setStatus("expired"); }
        else { setStatus("pending"); setTimeout(poll, 2500); }
      } catch { setStatus("error"); }
    };
    poll();
  }, [location.search]);

  const checkout = async (pkgId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout-session", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="billing-page">
      <PageHeader title="Billing & Plans" subtitle="Subscribe to unlock more seats, modules and support tiers. Powered by Stripe (test mode)." />

      {status && (
        <div className={`mt-6 p-4 border-2 ${status === "paid" ? "border-[#0B7A75] bg-[#0B7A75]/5" : status === "expired" ? "border-[#D90429] bg-[#D90429]/5" : "border-[#F5B700] bg-[#F5B700]/5"}`} data-testid="billing-status">
          {status === "paid" && "✓ Payment confirmed — your subscription is active."}
          {status === "pending" && "Payment processing… we'll refresh in a moment."}
          {status === "expired" && "Checkout session expired. Pick a plan again."}
          {status === "error" && "We couldn't verify the payment. Refresh in a few seconds."}
          {status === "timeout" && "Still processing — check back in a minute."}
          {status === "checking" && "Checking payment status…"}
        </div>
      )}

      {sub && (
        <div className="mt-6 border border-[#0A0A0A] bg-white p-5" data-testid="current-subscription">
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057]">Current plan</div>
          <div className="flex items-center justify-between mt-2">
            <div>
              <div className="text-2xl font-bold capitalize">{sub.plan || "free"}</div>
              <div className="text-xs text-[#495057]">{sub.status || "trial"} · {tenant?.name}</div>
            </div>
            {sub.status === "active" && <span className="text-[10px] uppercase tracking-[0.15em] border border-[#0B7A75] text-[#0B7A75] px-2 py-0.5">Active</span>}
          </div>
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="packages-grid">
        {packages.map((p, i) => (
          <div key={p.id} className={`border-2 ${p.id === "growth" ? "border-[#FF4F00] relative" : "border-[#0A0A0A]"} bg-white p-6 flex flex-col`} data-testid={`package-${p.id}`}>
            {p.id === "growth" && <div className="absolute -top-3 left-6 bg-[#FF4F00] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-0.5">Most popular</div>}
            <div className="text-xs uppercase tracking-[0.15em] text-[#495057]">{p.name}</div>
            <div className="mt-2 text-4xl font-bold tabular-nums">{p.display_currency || "GHS"} {(p.amount_ghs || p.amount).toLocaleString()}</div>
            <div className="text-xs text-[#495057]">per month <span className="text-[#FF4F00]">(charged USD {p.amount})</span></div>
            <ul className="mt-4 space-y-2 flex-1">
              {p.features.map((f, j) => (<li key={j} className="flex items-start gap-2 text-sm"><Check size={14} className="text-[#0B7A75] mt-0.5 shrink-0"/>{f}</li>))}
            </ul>
            <button onClick={() => checkout(p.id)} disabled={loading} data-testid={`subscribe-${p.id}`}
              className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium disabled:opacity-50 ${p.id === "growth" ? "bg-[#FF4F00] hover:bg-[#0A0A0A] text-white" : "bg-[#0A0A0A] hover:bg-[#FF4F00] text-white"}`}>
              <CreditCard size={14}/> {sub?.plan === p.id ? "Currently active" : "Subscribe"}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-[#495057]" data-testid="billing-note">
        <Sparkles size={12} className="inline text-[#FF4F00]"/> Stripe is in <span className="font-mono">sk_test_emergent</span> mode — use test card <span className="font-mono">4242 4242 4242 4242</span> with any future expiry and any CVC to simulate a successful subscription.
      </div>
    </div>
  );
}
